import type { CheckItem } from '../types';

const ALLOWED_EXT = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv', 'mkv']);
const VIDEO_FILENAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]*\.(mp4|webm|mov|m4v|ogv|mkv)$/i;

export type VideoRename = { from: string; to: string };

/** Repo filename on disk for a committed clip (handles unsaved renames). */
export function committedVideoRepoFilename(item: CheckItem, displayFilename: string): string {
  const pending = item.videoRenames?.find(r => r.to === displayFilename);
  return pending?.from ?? displayFilename;
}

export function sanitizeVideoFilenameInput(raw: string, fallbackExt: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let name = trimmed.replace(/[/\\]/g, '').replace(/\s+/g, '-');
  if (!/\./.test(name)) name = `${name}.${fallbackExt}`;

  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXT.has(ext)) return null;
  if (!VIDEO_FILENAME_RE.test(name)) return null;
  return name;
}

export function videoFilenamesInUse(item: CheckItem, exclude?: string): Set<string> {
  const names = new Set<string>();
  for (const f of item.video) {
    if (!item.videoToDelete.includes(f) && f !== exclude) names.add(f);
  }
  for (const p of item.pendingVideo) {
    if (p.filename !== exclude) names.add(p.filename);
  }
  return names;
}

export function queueVideoRename(item: CheckItem, from: string, to: string): void {
  if (from === to) return;
  if (!item.videoRenames) item.videoRenames = [];

  const chained = item.videoRenames.find(r => r.to === from);
  if (chained) {
    chained.to = to;
    if (chained.from === to) {
      item.videoRenames = item.videoRenames.filter(r => r !== chained);
    }
    return;
  }

  const undo = item.videoRenames.find(r => r.from === to);
  if (undo) {
    item.videoRenames = item.videoRenames.filter(r => r !== undo);
    return;
  }

  item.videoRenames = item.videoRenames.filter(r => r.from !== from);
  item.videoRenames.push({ from, to });
}

/** Apply a filename edit; returns new name, old name if unchanged, or null if invalid. */
export function applyVideoFilenameEdit(
  item: CheckItem,
  oldName: string,
  newRaw: string,
  isPending: boolean
): string | null {
  const ext = oldName.split('.').pop()?.toLowerCase() ?? 'mp4';
  const newName = sanitizeVideoFilenameInput(newRaw, ext);
  if (!newName) return null;
  if (newName === oldName) return oldName;
  if (videoFilenamesInUse(item, oldName).has(newName)) return null;

  if (isPending) {
    const pending = item.pendingVideo.find(p => p.filename === oldName);
    if (!pending) return null;
    pending.filename = newName;
    return newName;
  }

  const idx = item.video.indexOf(oldName);
  if (idx < 0) return null;
  item.video[idx] = newName;
  queueVideoRename(item, oldName, newName);
  return newName;
}
